import React, { useMemo, useState } from "react"
import { useQuery } from "react-query"
import { AxiosError } from "axios"
import { toast } from "react-toastify"
import MyAlert from "@common-utils/MyAlert"
import MyTooltip from "@common-utils/MyTooltip"
import { ICandidatDocumentModel, IHttpErrorResponseModel } from "@common-models/*"
import FileAndDriveHelper from "@common-helpers/FileAndDriveHelper"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import { getDocos } from "../../../core/_requests"

interface IProps {
     candidatId: number
}

const REACT_QUERY_KEY_GET_DOCOS_SUIVI_ENTRETIEN_AGENCE_QUERY = "REACT_QUERY_KEY_GET_DOCOS_SUIVI_ENTRETIEN_AGENCE_QUERY"

const DocosList: React.FC<IProps> = ({ candidatId }) => {
     const [selectedDoco, setSelectedDoco] = useState<{ item: ICandidatDocumentModel; mode: "view" | "edit" } | null>(null)
     const [iframeLoading, setIframeLoading] = useState<boolean>(false)

     const docosQuery = useQuery<ICandidatDocumentModel[], AxiosError>(REACT_QUERY_KEY_GET_DOCOS_SUIVI_ENTRETIEN_AGENCE_QUERY, () => {
          return getDocos(candidatId)
               .then(r => r.data)
               .catch((e: AxiosError) => {
                    const error: IHttpErrorResponseModel = e.response?.data
                    toast.error(error?.detail || "Erreur lors du chargement des documents", { autoClose: false })
                    throw e
               })
     })

     const DocumentViewer = useMemo(() => {
          if (!selectedDoco) return null

          const docUrl =
               selectedDoco.mode === "view"
                    ? FileAndDriveHelper.getUrlOfDriveFilePreview(selectedDoco.item.googleDriveID as string)
                    : FileAndDriveHelper.getUrlOfDriveFileEditMode(selectedDoco.item.googleDriveID as string)

          return (
               <div className="card h-100 border-0 shadow-none">
                    {/* Header */}
                    <div className="card-header min-h-50px px-0 border-0 mb-2">
                         <div className="card-title m-0">
                              <button type="button" className="btn btn-sm btn-icon btn-light-primary me-3" onClick={() => setSelectedDoco(null)}>
                                   <i className="fas fa-arrow-left fs-4" />
                              </button>
                              <div className="d-flex flex-column">
                                   <span className="fw-bolder fs-6 text-gray-800 text-truncate mw-200px mw-lg-300px">{selectedDoco.item.intitule}</span>
                              </div>
                         </div>
                         <div className="card-toolbar m-0">
                              <div className="d-flex align-items-center gap-2">
                                   <MyTooltip title={selectedDoco.mode === "view" ? "Passer en mode édition" : "Passer en mode lecture"}>
                                        <button
                                             type="button"
                                             className={`btn btn-sm ${selectedDoco.mode === "view" ? "btn-light-warning" : "btn-light-success"} fw-bold`}
                                             onClick={() => {
                                                  setIframeLoading(true)
                                                  setSelectedDoco(prev => (prev ? { ...prev, mode: prev.mode === "view" ? "edit" : "view" } : null))
                                             }}
                                        >
                                             {selectedDoco.mode === "view" ? (
                                                  <>
                                                       <i className="fas fa-pen me-2" /> Éditer
                                                  </>
                                             ) : (
                                                  <>
                                                       <i className="fas fa-eye me-2" /> Lecture
                                                  </>
                                             )}
                                        </button>
                                   </MyTooltip>
                              </div>
                         </div>
                    </div>

                    {/* Content Body */}
                    <div className="card-body p-0 flex-grow-1 position-relative border border-gray-300 rounded overflow-hidden">
                         {/* Manual Loading Overlay Logic */}
                         {iframeLoading && (
                              <div className="position-absolute w-100 h-100 d-flex justify-content-center align-items-center bg-white top-0 start-0 z-index-1">
                                   <MySimpleSpinner size="lg" />
                              </div>
                         )}

                         <iframe
                              src={docUrl}
                              className="w-100 h-100"
                              style={{ border: "none" }}
                              onLoad={() => setIframeLoading(false)}
                              title="Document Viewer"
                              sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                         />
                    </div>
               </div>
          )
     }, [selectedDoco, iframeLoading])

     const DocumentGrid = useMemo(() => {
          return (
               <div className="row g-4 animate__animated animate__fadeIn">
                    {docosQuery.data?.map((item, index) => (
                         <div className="col-sm-6 col-lg-4" key={index}>
                              <div
                                   className="card h-100 border border-dashed border-gray-300 hover-elevate-up cursor-pointer bg-hover-light-primary transition-300"
                                   onClick={() => {
                                        setIframeLoading(true)
                                        setSelectedDoco({ item, mode: "view" })
                                   }}
                              >
                                   <div className="card-body d-flex flex-column align-items-center justify-content-center text-center p-6">
                                        <div className="symbol symbol-60px mb-4">
                                             <span className="symbol-label bg-light-primary">
                                                  <i className="fas fa-file-word fs-1 text-primary"></i>
                                             </span>
                                        </div>
                                        <div className="fs-6 fw-bolder text-gray-800 text-hover-primary mb-1 text-truncate w-100">{item.intitule || `Document sans titre`}</div>
                                        <div className="fs-7 text-muted">Cliquez pour voir</div>
                                   </div>
                              </div>
                         </div>
                    ))}
               </div>
          )
     }, [docosQuery.data])

     return (
          <div className="d-flex flex-column h-100">
               {docosQuery.error && <MyAlert type={"danger"}>{docosQuery.error.response?.data.detail}</MyAlert>}

               {/* CHECK BOTH isLoading AND isFetching */}
               {docosQuery.isLoading || docosQuery.isFetching ? (
                    <div className="d-flex flex-center h-100">
                         <div className="d-flex flex-column align-items-center">
                              <span className="spinner-border text-primary mb-2" />
                              <span className="text-gray-500 fs-6">Chargement des documents...</span>
                         </div>
                    </div>
               ) : (
                    <>
                         {docosQuery.data && (
                              <div className="h-100 d-flex flex-column">
                                   {selectedDoco ? (
                                        DocumentViewer
                                   ) : (
                                        <div className="overflow-auto p-2 h-100">
                                             {docosQuery.data.length > 0 ? (
                                                  DocumentGrid
                                             ) : (
                                                  <div className="d-flex flex-center h-100 flex-column">
                                                       <i className="fas fa-folder-open fs-3x text-gray-300 mb-4" />
                                                       <div className="text-gray-400 fw-bold fs-5">Aucun document disponible</div>
                                                  </div>
                                             )}
                                        </div>
                                   )}
                              </div>
                         )}
                    </>
               )}
          </div>
     )
}

export default DocosList
